import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { ReleaseFormData } from '../../types';
import { useTranslation } from 'react-i18next';
import { AudioPlayer, Toast, useToast } from '../../components/shared';
import { MUSIC_LANGUAGES, DEFAULT_LANGUAGE } from '../../constants/languages';

const TrackEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [formData, setFormData] = useState<Partial<ReleaseFormData>>({
    tracks: [{
      title: '',
      duration: '',
      order: 1,
      track_number: 1,
      is_explicit: false,
      language: DEFAULT_LANGUAGE,
      has_lyrics: false,
      lyrics: '',
      ai_generated: {
        music: false,
        lyrics: false,
        vocals: false,
        human_contribution: true,
        human_notes: ''
      }
    }],
    type: 'Single',
    language: DEFAULT_LANGUAGE,
    partners: [],
    split_pay: [],
    rights: {
      isrc: '',
      upc: '',
      phonogram_owner: 'MusicHub',
      composition_owner: 'MusicHub'
    }
  });

  const navigate = useNavigate();
  const { releases, updateRelease, getAudioFileUrl, getCoverArtUrl } = useData();
  const { t } = useTranslation();
  const { toasts, hideToast, showSuccess, showError } = useToast();
  const release = releases.find(r => r.id === id);

  useEffect(() => {
    if (release) {
      setFormData({
        tracks: release.tracks.length > 0 ? release.tracks.map(track => ({
          title: track.title,
          duration: track.duration,
          order: track.order,
          track_number: track.track_number,
          is_explicit: track.is_explicit,
          language: track.language,
          has_lyrics: track.has_lyrics,
          lyrics: track.lyrics,
          ai_generated: track.ai_generated
        })) : [{
          title: '',
          duration: '',
          order: 1,
          track_number: 1,
          is_explicit: false,
          language: DEFAULT_LANGUAGE,
          has_lyrics: false,
          lyrics: '',
          ai_generated: {
            music: false,
            lyrics: false,
            vocals: false,
            human_contribution: true,
            human_notes: ''
          }
        }],
        title: release.title,
        type: release.type,
        release_date: release.release_date,
        genre: release.genre,
        subgenre: release.subgenre,
        language: release.language,
        description: release.description,
        concept_description: release.concept_description,
        partners: release.partners || [],
        split_pay: release.split_pay || [],
        rights: release.rights || {
          isrc: '',
          upc: '',
          phonogram_owner: 'MusicHub',
          composition_owner: 'MusicHub'
        }
      });
    }
  }, [release]);

  const steps = [
    { id: 1, name: t('release.trackInfo'), description: t('release.trackDetails') },
    { id: 2, name: t('release.releaseInfo'), description: t('release.albumEpSingle') },
    { id: 3, name: t('release.visualMaterials'), description: t('release.coverArt') },
    { id: 4, name: t('release.distribution'), description: t('release.platformSelection') },
    { id: 5, name: t('release.sharing'), description: t('release.revenueSharing') },
    { id: 6, name: t('release.summary'), description: t('release.finalReview') }
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const convertFormDataToRelease = (data: Partial<ReleaseFormData>) => {
    const { cover_art, cover_art_file, selected_artist_id, ...rest } = data;
    
    // Convert tracks to remove File types and add required fields
    const tracks = data.tracks?.map((track, index) => {
      const { audio_file, ...trackRest } = track;
      return {
        ...trackRest,
        id: release?.tracks[index]?.id || `temp-${Date.now()}-${index}`,
        release_id: release?.id || ''
      };
    }) || [];

    // Add release_id to rights if exists
    const rights = data.rights ? {
      ...data.rights,
      release_id: release?.id || ''
    } : undefined;

    // Add id and release_id to contributors if exists
    const contributors = data.contributors?.map((contributor, index) => ({
      ...contributor,
      id: release?.contributors?.[index]?.id || `temp-contrib-${Date.now()}-${index}`,
      release_id: release?.id || ''
    }));

    // Add id and release_id to split_pay if exists
    const split_pay = data.split_pay?.map((split, index) => ({
      ...split,
      id: release?.split_pay?.[index]?.id || `temp-split-${Date.now()}-${index}`,
      release_id: release?.id || ''
    }));

    return {
      ...rest,
      tracks,
      rights,
      contributors,
      split_pay
    };
  };

  const handleSubmit = () => {
    if (!release) return;

    try {
      const releaseData = convertFormDataToRelease(formData);
      updateRelease(release.id, {
        ...releaseData,
        status: 'pending',
        cover_art_file: formData.cover_art_file,
        tracks: formData.tracks?.map((track, index) => ({
          ...track,
          id: release?.tracks[index]?.id || `temp-${Date.now()}-${index}`,
          release_id: release?.id || ''
        })) || []
      });
      showSuccess(t('tracks.trackUpdated'));
      navigate('/tracks');
    } catch (error) {
      console.error('Track update error:', error);
      showError('Müzik güncellenirken hata oluştu.');
    }
  };

  const saveDraft = () => {
    if (!release) return;

    try {
      const releaseData = convertFormDataToRelease(formData);
      updateRelease(release.id, {
        ...releaseData,
        cover_art_file: formData.cover_art_file,
        tracks: formData.tracks?.map((track, index) => ({
          ...track,
          id: release?.tracks[index]?.id || `temp-${Date.now()}-${index}`,
          release_id: release?.id || ''
        })) || []
      });
      showSuccess(t('tracks.draftSaved'));
      navigate('/tracks');
    } catch (error) {
      console.error('Draft save error:', error);
      showError('Taslak kaydedilirken hata oluştu.');
    }
  };

  if (!release) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="glass-card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-white/60 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
                      <h3 className="text-lg font-medium text-white mb-2">{t('messages.musicNotFound')}</h3>
            <p className="text-white/70">{t('messages.musicNotAvailable')}</p>
          <button
            onClick={() => navigate('/tracks')}
            className="btn-primary mt-4"
          >
            {t('tracks.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
              </svg>
{t('release.trackInfo')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('tracks.trackName')}
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.tracks?.[0]?.title || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    tracks: [{
                      ...prev.tracks?.[0]!,
                      title: e.target.value
                    }]
                  }))}
                  placeholder={t('release.enterTrackName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('audio.audioPreview')} (WAV)
                </label>
                
                {/* File Selection - Only show if no audio file and no existing audio URL */}
                {!formData.tracks?.[0]?.audio_file && !getAudioFileUrl(`${release?.id}_track_1`) && !isFileUploading && (
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-white/30 border-dashed rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200">
                    <div className="space-y-1 text-center">
                      <svg className="mx-auto h-12 w-12 text-white/60" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M12 14a4 4 0 014-4h8a4 4 0 014 4v10a4 4 0 01-4 4h-8a4 4 0 01-4-4V14zm6 0a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2h-8a2 2 0 01-2-2V14zm2 6v4l3-2-3-2z" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-white/70">
                        <label className="relative cursor-pointer bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg px-4 py-2 font-medium text-white transition-colors duration-200">
                          <span>{t('release.selectWavFile')}</span>
                          <input 
                            type="file" 
                            className="sr-only" 
                            accept=".wav,audio/wav"
                            disabled={isFileUploading}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setIsFileUploading(true);
                                try {
                                  await new Promise(resolve => setTimeout(resolve, 500));
                                  setFormData(prev => ({
                                    ...prev,
                                    tracks: [{
                                      ...prev.tracks?.[0]!,
                                      audio_file: file
                                    }]
                                  }));
                                } finally {
                                  setIsFileUploading(false);
                                }
                              }
                            }}
                          />
                        </label>
                        <p className="pl-1 text-white/60">{t('release.dragDropOrSelect')}</p>
                      </div>
                      <p className="text-xs text-white/50">
                        {t('release.wavFileOnly')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {isFileUploading && (
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-orange-500/50 border-dashed rounded-xl bg-orange-500/10 transition-all duration-200">
                    <div className="space-y-1 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                      <p className="text-orange-300 font-medium">{t('audio.uploadingFile')}</p>
                      <p className="text-white/60 text-sm">{t('tracks.pleaseWait')}</p>
                    </div>
                  </div>
                )}

                {/* Audio Player - Show if new file uploaded OR existing audio URL */}
                {(formData.tracks?.[0]?.audio_file || getAudioFileUrl(`${release?.id}_track_1`)) && !isFileUploading && (
                  <div className="space-y-4">
                    <AudioPlayer
                      file={formData.tracks?.[0]?.audio_file instanceof File ? formData.tracks?.[0]?.audio_file : null}
                      audioUrl={!formData.tracks?.[0]?.audio_file ? getAudioFileUrl(`${release?.id}_track_1`) : undefined}
                      fileName={formData.tracks?.[0]?.title || 'Audio File'}
                      onDurationCalculated={(duration) => {
                        setFormData(prev => ({
                          ...prev,
                          tracks: [{
                            ...prev.tracks?.[0]!,
                            duration: duration
                          }]
                        }));
                      }}
                    />
                    
                    {/* Change File Button */}
                    <div className="flex justify-center">
                      <label className="cursor-pointer bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors duration-200">
                        <span>{t('common.change')} WAV {t('common.file')}</span>
                        <input 
                          type="file" 
                          className="sr-only" 
                          accept=".wav,audio/wav"
                          disabled={isFileUploading}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setIsFileUploading(true);
                              try {
                                await new Promise(resolve => setTimeout(resolve, 500));
                                setFormData(prev => ({
                                  ...prev,
                                  tracks: [{
                                    ...prev.tracks?.[0]!,
                                    audio_file: file,
                                    duration: '' // Reset duration for new file
                                  }]
                                }));
                              } finally {
                                setIsFileUploading(false);
                              }
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('tracks.duration')}
                    {formData.tracks?.[0]?.duration && (
                      <span className="ml-2 text-xs text-green-400">({t('audio.autoCalculated')})</span>
                    )}
                  </label>
                  <input
                    type="text"
                    className={`input-field ${formData.tracks?.[0]?.duration ? 'bg-green-500/10 border-green-500/30' : ''}`}
                    value={formData.tracks?.[0]?.duration || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracks: [{
                        ...prev.tracks?.[0]!,
                        duration: e.target.value
                      }]
                    }))}
                    placeholder="3:45"
                    readOnly={!!formData.tracks?.[0]?.audio_file}
                    title={formData.tracks?.[0]?.audio_file ? t('audio.autoCalculated') : ''}
                  />
                  {formData.tracks?.[0]?.audio_file && (
                    <p className="text-xs text-green-300 mt-1">
                      {t('audio.autoCalculated')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('tracks.trackOrder')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="input-field"
                    value={formData.tracks?.[0]?.track_number || 1}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracks: [{
                        ...prev.tracks?.[0]!,
                        track_number: parseInt(e.target.value)
                      }]
                    }))}
                    placeholder="1"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('release.trackLanguage')}
                </label>
                <select 
                  className="input-field"
                  value={formData.tracks?.[0]?.language || 'Turkish'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    tracks: [{
                      ...prev.tracks?.[0]!,
                      language: e.target.value
                    }]
                  }))}
                >
                  {MUSIC_LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="explicit"
                    className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                    checked={formData.tracks?.[0]?.is_explicit || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracks: [{
                        ...prev.tracks?.[0]!,
                        is_explicit: e.target.checked
                      }]
                    }))}
                  />
                  <label htmlFor="explicit" className="ml-2 text-sm text-white">
                    {t('tracks.explicit')}
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="has_lyrics"
                    className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                    checked={formData.tracks?.[0]?.has_lyrics || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracks: [{
                        ...prev.tracks?.[0]!,
                        has_lyrics: e.target.checked
                      }]
                    }))}
                  />
                  <label htmlFor="has_lyrics" className="ml-2 text-sm text-white">
                    {t('tracks.hasLyricsLabel')}
                  </label>
                </div>
              </div>

              {formData.tracks?.[0]?.has_lyrics && (
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('tracks.lyrics')}
                  </label>
                  <textarea
                    rows={6}
                    className="input-field resize-none"
                    value={formData.tracks?.[0]?.lyrics || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tracks: [{
                        ...prev.tracks?.[0]!,
                        lyrics: e.target.value
                      }]
                    }))}
                    placeholder={t('release.enterLyrics')}
                  />
                </div>
              )}

              {/* AI Katkısı Bilgileri */}
              <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-4">
                <h4 className="text-sm font-medium text-purple-200 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {t('admin.aiContribution')}
                </h4>
                <p className="text-xs text-purple-300 mb-3">{t('admin.whichPartsAi')}</p>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ai_music"
                      className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                      checked={formData.tracks?.[0]?.ai_generated?.music || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        tracks: [{
                          ...prev.tracks?.[0]!,
                          ai_generated: {
                            ...prev.tracks?.[0]?.ai_generated!,
                            music: e.target.checked
                          }
                        }]
                      }))}
                    />
                    <label htmlFor="ai_music" className="ml-2 text-sm text-white">
                      {t('admin.musicAi')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ai_lyrics"
                      className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                      checked={formData.tracks?.[0]?.ai_generated?.lyrics || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        tracks: [{
                          ...prev.tracks?.[0]!,
                          ai_generated: {
                            ...prev.tracks?.[0]?.ai_generated!,
                            lyrics: e.target.checked
                          }
                        }]
                      }))}
                    />
                    <label htmlFor="ai_lyrics" className="ml-2 text-sm text-white">
                      {t('admin.lyricsAi')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ai_vocals"
                      className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                      checked={formData.tracks?.[0]?.ai_generated?.vocals || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        tracks: [{
                          ...prev.tracks?.[0]!,
                          ai_generated: {
                            ...prev.tracks?.[0]?.ai_generated!,
                            vocals: e.target.checked
                          }
                        }]
                      }))}
                    />
                    <label htmlFor="ai_vocals" className="ml-2 text-sm text-white">
                      {t('admin.vocalsAi')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="human_contribution"
                      className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                      checked={formData.tracks?.[0]?.ai_generated?.human_contribution || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        tracks: [{
                          ...prev.tracks?.[0]!,
                          ai_generated: {
                            ...prev.tracks?.[0]?.ai_generated!,
                            human_contribution: e.target.checked
                          }
                        }]
                      }))}
                    />
                    <label htmlFor="human_contribution" className="ml-2 text-sm text-white">
                      {t('admin.humanContribution')}
                    </label>
                  </div>

                  {formData.tracks?.[0]?.ai_generated?.human_contribution && (
                    <div>
                      <label className="block text-xs font-medium text-white mb-1">
                        {t('admin.explainHumanContribution')}
                      </label>
                      <textarea
                        rows={2}
                        className="input-field text-sm resize-none"
                        value={formData.tracks?.[0]?.ai_generated?.human_notes || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          tracks: [{
                            ...prev.tracks?.[0]!,
                            ai_generated: {
                              ...prev.tracks?.[0]?.ai_generated!,
                              human_notes: e.target.value
                            }
                          }]
                        }))}
                        placeholder={t('release.humanContributionExample')}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              {t('release.releaseInfo')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('release.releaseTitle')}
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                      placeholder={t('release.enterAlbumName')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('release.releaseType')}
                  </label>
                  <select 
                    className="input-field"
                    value={formData.type || 'Single'}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'Single' | 'EP' | 'Album' }))}
                  >
                    <option value="Single">Single</option>
                    <option value="EP">EP</option>
                    <option value="Album">Albüm</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('release.releaseDate')}
                  </label>
                  <input
                    type="date"
                    className="input-field"
                    value={formData.release_date || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, release_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('release.mainGenre')}
                  </label>
                  <select 
                    className="input-field"
                    value={formData.genre || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, genre: e.target.value }))}
                  >
                    <option value="">{t('release.selectGenre')}</option>
                    <option value="Pop">Pop</option>
                    <option value="Rock">Rock</option>
                    <option value="Hip Hop">Hip Hop</option>
                    <option value="Electronic">Electronic</option>
                    <option value="Folk">Folk</option>
                    <option value="Classical">Klasik</option>
                    <option value="Jazz">Jazz</option>
                    <option value="Alternative">Alternative</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('release.subGenre')}
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.subgenre || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, subgenre: e.target.value }))}
                    placeholder={t('release.genreExample')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('release.releaseLanguage')}
                </label>
                <select 
                  className="input-field"
                  value={formData.language || DEFAULT_LANGUAGE}
                  onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                >
                  {MUSIC_LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('release.visualMaterials')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('release.coverImage')}
                </label>
                
                {/* Cover Art Preview */}
                {(formData.cover_art_file instanceof File || getCoverArtUrl(release.id)) && (
                  <div className="mb-4 p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <img 
                          src={formData.cover_art_file instanceof File 
                            ? URL.createObjectURL(formData.cover_art_file)
                            : getCoverArtUrl(release.id) || ''
                          } 
                          alt="Cover Art Preview" 
                          className="w-20 h-20 object-cover rounded-lg border border-white/20"
                          onError={(e) => {
                            console.error('Cover art yüklenemedi:', getCoverArtUrl(release.id));
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">
                          {t('tracks.coverArtPreview')}
                        </p>
                        <p className="text-white/60 text-sm">
                          {formData.cover_art_file instanceof File 
                            ? `${t('tracks.newFile')}: ${formData.cover_art_file.name}`
                            : t('tracks.currentCoverArt')
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* File Selection */}
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-white/30 border-dashed rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200">
                  <div className="space-y-1 text-center">
                    <svg className="mx-auto h-12 w-12 text-white/60" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-white/70">
                      <label className="relative cursor-pointer bg-orange-500 hover:bg-orange-600 rounded-lg px-4 py-2 font-medium text-white transition-colors duration-200">
                        <span>{(formData.cover_art_file instanceof File || getCoverArtUrl(release.id)) ? t('tracks.changeCover') : t('tracks.selectImage')}</span>
                        <input 
                          type="file" 
                          className="sr-only" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setFormData(prev => ({
                                ...prev,
                                cover_art_file: file
                              }));
                            }
                          }}
                        />
                      </label>
                      <p className="pl-1 text-white/60">{t('tracks.orDragDrop')}</p>
                    </div>
                    <p className="text-xs text-white/50">
                      {t('tracks.jpegFormat')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('tracks.description')}
                </label>
                <textarea
                  rows={4}
                  className="input-field resize-none"
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                      placeholder={t('release.releaseDescription')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('tracks.conceptThemeDescription')}
                  <span className="text-white/50 font-normal text-xs ml-2">({t('tracks.optional')})</span>
                </label>
                <textarea
                  rows={3}
                  className="input-field resize-none"
                  value={formData.concept_description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, concept_description: e.target.value }))}
                                      placeholder={t('release.conceptTheme')}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              {t('release.distributionChannels')}
            </h3>
            <div className="space-y-4">
              <p className="text-white/70">
                {t('release.platformQuestion')}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Spotify', 'Apple Music', 'YouTube Music', 'Amazon Music',
                  'Deezer', 'Tidal', 'SoundCloud', 'Bandcamp'
                ].map((platform) => (
                  <div key={platform} className="flex items-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <input
                      type="checkbox"
                      id={platform}
                      className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                      checked={formData.partners?.includes(platform) || false}
                      onChange={(e) => {
                        const partners = formData.partners || [];
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            partners: [...partners, platform]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            partners: partners.filter(p => p !== platform)
                          }));
                        }
                      }}
                    />
                    <label htmlFor={platform} className="ml-3 text-sm text-white">
                      {platform}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              {t('release.revenueSharing')}
            </h3>
            <div className="space-y-4">
              <p className="text-white/70">
                {t('release.revenueDescription')}
              </p>

              <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-200">
                      {t('admin.defaultRevenue')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="split_payment"
                  className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-white/30 rounded bg-white/10 backdrop-blur-sm"
                  checked={formData.split_pay && formData.split_pay.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData(prev => ({
                        ...prev,
                        split_pay: [{
                          collaborator_name: '',
                          collaborator_url: '',
                          email: '',
                          percentage: 50,
                          role: 'Producer'
                        }]
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        split_pay: []
                      }));
                    }
                  }}
                />
                <label htmlFor="split_payment" className="ml-2 text-sm text-white">
                  {t('tracks.shareRevenueWithOthers')}
                </label>
              </div>

              {formData.split_pay && formData.split_pay.length > 0 && (
                <div className="space-y-4">
                  {formData.split_pay.map((split, index) => (
                    <div key={index} className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            {t('tracks.collaboratorName')}
                          </label>
                          <input
                            type="text"
                            className="input-field"
                            value={split.collaborator_name}
                            onChange={(e) => {
                              const newSplits = [...(formData.split_pay || [])];
                              newSplits[index] = { ...newSplits[index], collaborator_name: e.target.value };
                              setFormData(prev => ({ ...prev, split_pay: newSplits }));
                            }}
                            placeholder={t('release.collaboratorName')}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            {t('tracks.collaboratorEmail')}
                          </label>
                          <input
                            type="email"
                            className="input-field"
                            value={split.email}
                            onChange={(e) => {
                              const newSplits = [...(formData.split_pay || [])];
                              newSplits[index] = { ...newSplits[index], email: e.target.value };
                              setFormData(prev => ({ ...prev, split_pay: newSplits }));
                            }}
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            {t('tracks.collaboratorPercentage')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            className="input-field"
                            value={split.percentage}
                            onChange={(e) => {
                              const newSplits = [...(formData.split_pay || [])];
                              newSplits[index] = { ...newSplits[index], percentage: parseInt(e.target.value) };
                              setFormData(prev => ({ ...prev, split_pay: newSplits }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('release.summaryAndSubmit')}
            </h3>
            <div className="space-y-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h4 className="text-lg font-medium text-white mb-4">{t('release.releaseSummary')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/70">{t('admin.releaseName')}:</span>
                    <span className="ml-2 text-white">{formData.title || t('messages.notSpecified')}</span>
                  </div>
                  <div>
                    <span className="text-white/70">{t('admin.type')}:</span>
                    <span className="ml-2 text-white">{formData.type}</span>
                  </div>
                  <div>
                    <span className="text-white/70">{t('admin.category')}:</span>
                    <span className="ml-2 text-white">{formData.genre || t('messages.notSpecified')}</span>
                  </div>
                  <div>
                    <span className="text-white/70">{t('admin.releaseDate')}:</span>
                    <span className="ml-2 text-white">{formData.release_date || t('messages.notSpecified')}</span>
                  </div>
                  <div>
                    <span className="text-white/70">{t('admin.platformCount')}:</span>
                                          <span className="ml-2 text-white">{formData.partners?.length || 0} {t('admin.platform')}</span>
                  </div>
                  <div>
                    <span className="text-white/70">{t('common.status')}:</span>
                    <span className="ml-2 text-yellow-300">{t('common.updatingStatus')}</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-200">
                      {t('messages.adminApprovalInfo')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('tracks.edit')}</h1>
        <p className="mt-1 text-sm text-white/70">
          {release.title} - {release.type} {t('messages.editing')}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                  currentStep === step.id
                    ? 'bg-orange-500 text-white shadow-lg'
                    : currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : 'bg-white/10 text-white/60 border border-white/20'
                }`}
              >
                {currentStep > step.id ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  step.id
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-12 h-0.5 mx-2 transition-all duration-200 ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-white/20'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-2">
          <p className="text-sm text-white/70">
            {t('release.stepProgress', { current: currentStep, total: steps.length, description: steps[currentStep - 1]?.description })}
          </p>
        </div>
      </div>

      {/* Form Content */}
      <div className="glass-card mb-8">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
                  <button
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.previous')}
          </button>
        
        <div className="flex space-x-3">
          <button
            onClick={saveDraft}
            className="btn-secondary"
          >
            {t('common.saveDraft')}
          </button>
          
          <button
            onClick={() => navigate('/tracks')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          
          {currentStep === steps.length ? (
            <button
              onClick={handleSubmit}
              className="btn-primary"
            >
              {t('common.updateAndSubmit')}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="btn-primary"
            >
              {t('common.next')}
            </button>
          )}
        </div>
      </div>
      
      {/* Toast Messages */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default TrackEdit; 